export const availableFirst = (searchResults) => searchResults.sort((a, b) => {
  return Number(b.isAvailable) - Number(a.isAvailable);
});

export const NEAR_ME_RADIUS_MILES = 1;

export const withinMiles = (results, origin, miles) => results.filter((result) => {
  const lat = Number(result.lot?.latitude);
  const lng = Number(result.lot?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && distanceMiles(origin, { lat, lng }) <= miles;
});

export const formatMiles = (miles) => {
  if (!Number.isFinite(miles)) return '';
  if (miles < 0.1) return 'Under 0.1 mi';
  return `${miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi`;
};

export const SORT_OPTIONS = [
  { value: 'recommended', label: 'Recommended' },
  { value: 'price-asc', label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
  { value: 'distance', label: 'Distance: nearest' },
];

const toRadians = (degrees) => (degrees * Math.PI) / 180;

export const distanceMiles = (from, to) => {
  const dLat = toRadians(to.lat - from.lat);
  const dLng = toRadians(to.lng - from.lng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(dLng / 2) ** 2;
  return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const sortResults = (results, sortBy, getPrice, origin = null) => {
  const sorted = [...results];

  if (sortBy === 'distance' && origin) {
    const away = (result) => {
      const lat = Number(result.lot?.latitude);
      const lng = Number(result.lot?.longitude);
      return Number.isFinite(lat) && Number.isFinite(lng) ? distanceMiles(origin, { lat, lng }) : Infinity;
    };
    return sorted.sort((a, b) => Number(b.isAvailable) - Number(a.isAvailable) || away(a) - away(b));
  }

  if (sortBy !== 'price-asc' && sortBy !== 'price-desc') return sorted;

  const direction = sortBy === 'price-asc' ? 1 : -1;
  const price = (result) => parseFloat(getPrice(result)) || 0;

  return sorted.sort((a, b) => (
    Number(b.isAvailable) - Number(a.isAvailable) || direction * (price(a) - price(b))
  ));
};

let pendingSearches = 0;

export const withSearchLoading = async (facilitySearch, run) => {
  pendingSearches++;
  facilitySearch.setLoading(true);
  try {
    return await run();
  } finally {
    pendingSearches--;
    if (!pendingSearches) facilitySearch.setLoading(false);
  }
};

// Each parking option's own name: a partner's parking type ("Covered, Self"), or our lot's name ("The Landing - Valet")
export const optionName = (result) => (result.provider === 'RESERVATIONS_LAB'
  ? result.lot?.parkingSchedules?.[0]?.name
  : null) || result.lot?.name;

// One result per location: the cheapest option that can be booked for these dates, carrying all of them as `options`
export const groupByLocation = (results, getPrice, canBook = (result) => result.isAvailable, hourlyOnly = () => false) => {
  const groups = new Map();
  for (const result of results) {
    // Per-hour prices can't be compared with per-stay prices, so hourly-only options never share a card
    const key = `${result.lot?.facility?.id || result.lot?.id}|${hourlyOnly(result) ? 'hourly' : 'stay'}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(result);
  }
  const price = (result) => parseFloat(getPrice(result)) || Infinity;
  return [...groups.values()].map((options) => {
    if (options.length === 1) return options[0];
    const ranked = options
      .map((option) => ({ ...option, bookable: canBook(option) }))
      .sort((a, b) => Number(b.bookable) - Number(a.bookable) || price(a) - price(b));
    return { ...ranked[0], options: ranked };
  });
};
